// PlayFirst Parent — Monthly profile-refresh card.
// Surfaces on the home page when the child's language profile hasn't been
// touched in 30+ days. 3 lightweight prompts, all optional. Submitting
// resets the clock so the card hides again until the next cycle.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Loader2, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export default function ProfileRefreshCard({ childId, onSubmitted }) {
  const [status, setStatus] = useState(null);   // { due, days_since_last_update }
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({
    new_gestalts: '',
    new_words: '',
    new_interests: '',
    tried_aac: null,
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!childId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/parent-app/children/${childId}/profile-refresh/status`,
          auth(),
        );
        if (!cancelled) setStatus(r.data);
      } catch {
        /* silent — non-critical surface */
      }
    })();
    return () => { cancelled = true; };
  }, [childId]);

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/parent-app/children/${childId}/profile-refresh`,
        form,
        auth(),
      );
      toast.success('Thanks — your child&apos;s profile is fresh again 🌱');
      setStatus({ ...status, due: false });
      setExpanded(false);
      setForm({ new_gestalts: '', new_words: '', new_interests: '', tried_aac: null, notes: '' });
      if (onSubmitted) onSubmitted();
    } catch {
      toast.error("Couldn't save — please try again");
    } finally {
      setSaving(false);
    }
  };

  if (!childId || !status?.due || dismissed) return null;

  return (
    <section
      className="rounded-3xl bg-gradient-to-br from-rose-50 via-orange-50/60 to-violet-50 border border-rose-200/70 p-5 relative overflow-hidden"
      data-testid="profile-refresh-card"
    >
      <button
        onClick={() => setDismissed(true)}
        className="absolute top-3 right-3 w-7 h-7 rounded-full hover:bg-white/60 inline-flex items-center justify-center text-stone-500"
        data-testid="profile-refresh-dismiss"
        title="Dismiss for now"
        type="button"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <div className="flex items-start gap-3 mb-3 pr-6">
        <div className="w-10 h-10 rounded-2xl bg-white/70 text-rose-700 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-serif text-lg text-[#3a2f4f]">
            What&apos;s emerged this month?
          </h3>
          <p className="text-xs text-stone-700/90 mt-0.5">
            It&apos;s been about {status.days_since_last_update} days since we last refreshed
            your child&apos;s language profile. A 30-second check-in keeps the play
            ideas, tips and stage content tuned to who they are right now.
          </p>
        </div>
      </div>

      {!expanded ? (
        <div className="flex gap-2">
          <button
            onClick={() => setExpanded(true)}
            className="h-10 px-4 rounded-2xl bg-[#7C6F9C] hover:bg-[#6a5d8a] text-white text-sm font-medium"
            data-testid="profile-refresh-start"
            type="button"
          >
            Quick refresh
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="h-10 px-4 rounded-2xl text-sm text-stone-500 hover:bg-white/40"
            data-testid="profile-refresh-later"
            type="button"
          >
            Maybe later
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="profile-refresh-form">
          <Mini
            label="Heard any new scripts, songs or gestalts? (optional)"
            value={form.new_gestalts}
            onChange={(v) => setForm({ ...form, new_gestalts: v })}
            placeholder="e.g. 'to infinity and beyond!' from Toy Story"
            testid="profile-refresh-gestalts"
          />
          <Mini
            label="Any new single words or short phrases? (optional)"
            value={form.new_words}
            onChange={(v) => setForm({ ...form, new_words: v })}
            placeholder="e.g. 'snack', 'more bubbles'"
            testid="profile-refresh-words"
          />
          <Mini
            label="New favourite interests? (comma-separated, optional)"
            value={form.new_interests}
            onChange={(v) => setForm({ ...form, new_interests: v })}
            placeholder="e.g. dinosaurs, water play"
            testid="profile-refresh-interests"
          />
          <fieldset className="text-xs">
            <legend className="text-stone-700 mb-1.5">
              Started exploring AAC, a communication board or talker since last month?
            </legend>
            <div className="flex gap-2">
              {[
                { v: true, label: 'Yes' },
                { v: false, label: 'No' },
                { v: null, label: 'Not sure' },
              ].map((opt) => (
                <button
                  key={String(opt.v)}
                  type="button"
                  onClick={() => setForm({ ...form, tried_aac: opt.v })}
                  className={`px-3 h-9 rounded-full border text-xs ${
                    form.tried_aac === opt.v
                      ? 'bg-[#7C6F9C] text-white border-[#7C6F9C]'
                      : 'bg-white text-stone-700 border-stone-200 hover:bg-stone-50'
                  }`}
                  data-testid={`profile-refresh-aac-${opt.label.toLowerCase().replace(' ','-')}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </fieldset>
          <Mini
            label="Anything else you'd love us to know? (optional)"
            value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })}
            placeholder="A new milestone, something hard, anything"
            testid="profile-refresh-notes"
            textarea
          />
          <div className="flex gap-2">
            <button
              onClick={submit}
              disabled={saving}
              className="h-10 px-5 rounded-2xl bg-[#7C6F9C] hover:bg-[#6a5d8a] text-white text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="profile-refresh-submit"
              type="button"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Save & refresh
            </button>
            <button
              onClick={() => setExpanded(false)}
              className="h-10 px-3 rounded-2xl text-sm text-stone-500 hover:bg-white/40"
              type="button"
              data-testid="profile-refresh-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function Mini({ label, value, onChange, placeholder, testid, textarea }) {
  const Tag = textarea ? 'textarea' : 'input';
  return (
    <label className="block">
      <span className="text-xs text-stone-700">{label}</span>
      <Tag
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={textarea ? 2 : undefined}
        className="mt-1 w-full px-3 py-2 rounded-2xl border border-stone-200 bg-white text-sm focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
        data-testid={testid}
      />
    </label>
  );
}
